import type { RecordOf } from 'immutable';
import type { UnknownAction } from 'redux';

import { getLogger } from '../../utils/core';
import { createDialog, moveQueuedDialogBefore, removeDialog, showNextDialog, updateDialogProps } from './rootUiActions';
import { mkRootUiData, mkRootUiStore, type RootUiData, type RootUiStore } from './rootUiTypes';

export { REDUCER_KEY } from './rootUiTypes';

const log = getLogger('flux/root-ui actions');

let nextId = 1;

const findById = (id: string | 0) => (object: RootUiData) => object.id === id;

/**
 * This reducer changes the visibility of a dialog in the store by ID. If no ID is provided,
 * the dialog on the top of the stack is updated.
 */
function removeDialogWithAnimation(state: RecordOf<RootUiStore>, dialogId: string | 0) {
  log.info(`dismiss dialog id=${dialogId}`);

  // when removing, optimize primary use case of popping last dialog pushed
  const activeModal = state.activeModals.last();
  if (activeModal != null && (dialogId === 0 || activeModal.id === dialogId)) {
    return state.setIn(['activeModals', -1, 'visible'], false);
  }

  const predicate = findById(dialogId);

  // not really sure if this is a valid use-case or not...
  let index = state.activeModals.findIndex(predicate);
  if (index >= 0) {
    return state.setIn(['activeModals', index, 'visible'], false);
  }

  index = state.queuedModals.findIndex(predicate);
  if (index >= 0) {
    return state.setIn(['queuedModals', index, 'visible'], false);
  }

  log.warn('Tried to dismiss non-existing dialog', dialogId);
  return state;
}

/**
 * This reducer removes a dialog from the store by ID. If no ID is provided,
 * the dialog on the top of the stack is removed.
 */
function removeDialogWithoutAnimation(state: RecordOf<RootUiStore>, dialogId: string | 0) {
  log.info(`remove dialog id=${dialogId}`);

  // when removing, optimize primary use case of popping last dialog pushed
  const activeModal = state.activeModals.last();
  if (activeModal != null && (dialogId === 0 || activeModal.id === dialogId)) {
    return state.update('activeModals', (modals) => modals.pop());
  }

  // not really sure if this is a valid use-case or not...
  const predicate = findById(dialogId);
  let index = state.activeModals.findIndex(predicate);
  if (index >= 0) {
    return state.update('activeModals', (modals) => modals.splice(index, 1));
  }

  index = state.queuedModals.findIndex(predicate);
  if (index >= 0) {
    return state.update('queuedModals', (modals) => modals.splice(index, 1));
  }

  log.warn('Tried to remove non-existing dialog', dialogId);
  return state;
}

export function reducer(state: RecordOf<RootUiStore> = mkRootUiStore(), action: UnknownAction) {
  switch (true) {
    case createDialog.match(action): {
      const uiData = action.payload;
      log.debug('creating dialog', uiData.type, uiData.id); // careful with circular deps in uiData.props
      let uiDataWithId = uiData;
      if (!uiData.id || uiData.id === '0') {
        uiDataWithId = uiData.merge({ id: `ROOT_DIALOG_${nextId}` });
        nextId += 1;
      } else {
        // do not process if a dialog with the same ID is already on the active or queued stacks
        if (
          state.activeModals.findIndex((x) => x.id === uiDataWithId.id) !== -1 ||
          state.queuedModals.findIndex((x) => x.id === uiDataWithId.id) !== -1
        ) {
          // log.debug('Dialog is already displayed or queued', uiDataWithId);
          return state;
        }
      }
      // log.debug('Done with check...')

      // ensure that DIALOG_ALERT modals are at the end of the array (which shows them on top) but preserve the order of other modal
      const modalArrayUpdater = (modals: Immutable.List<RecordOf<RootUiData>>, uiDataWithId: RecordOf<RootUiData>) =>
        modals.push(uiDataWithId).sort((a, b) => Number(a.type === 'DIALOG_ALERT') - Number(b.type === 'DIALOG_ALERT'));

      if (state.activeModals.size === 0 || uiData.allowNesting) {
        return state.update('activeModals', (modals) => modalArrayUpdater(modals, uiDataWithId));
      }
      return state.update('queuedModals', (modals) => modalArrayUpdater(modals, uiDataWithId));
    }
    case removeDialog.match(action): {
      const dialogId = action.payload ?? 0;
      if (
        action.meta?.onlyIfHidden &&
        state.activeModals.concat(state.queuedModals).find(findById(dialogId))?.visible
      ) {
        return state;
      }
      if (action.meta?.animate) {
        return removeDialogWithAnimation(state, dialogId);
      }
      return removeDialogWithoutAnimation(state, dialogId);
    }
    case showNextDialog.match(action): {
      log.info('Show next dialog if available.');
      // if we have no more active-modals, and we have a queued modal, then move first queued modal to active modals list
      if (state.activeModals.size === 0) {
        const queuedModal = state.queuedModals.get(0);
        if (queuedModal != null) {
          return state.merge({
            activeModals: state.activeModals.push(queuedModal),
            queuedModals: state.queuedModals.delete(0),
          });
        }
      }
      return state;
    }
    case updateDialogProps.match(action): {
      const { id, props } = action.payload;

      // check active list first
      const indexInActiveModals = state.activeModals.findIndex((x) => x.id === id);
      if (indexInActiveModals !== -1) {
        return state.set(
          'activeModals',
          state.activeModals.update(indexInActiveModals, (modal) =>
            (modal ?? mkRootUiData()).mergeIn(['props'], props),
          ),
        );
      }

      // check queued list if we didn't find one in active list
      const indexInQueuedModals = state.queuedModals.findIndex((x) => x.id === id);
      if (indexInQueuedModals !== -1) {
        return state.set(
          'queuedModals',
          state.queuedModals.update(indexInQueuedModals, (modal) =>
            (modal ?? mkRootUiData()).mergeIn(['props'], props),
          ),
        );
      }

      // do nothing if we couldn't find dialog
      return state;
    }
    case moveQueuedDialogBefore.match(action): {
      const { id, beforeId } = action.payload;

      if (!id || !beforeId || id === beforeId) {
        return state;
      }

      const fromIndex = state.queuedModals.findIndex((x: RecordOf<RootUiData>) => x.id === id);
      const toIndex = state.queuedModals.findIndex((x: RecordOf<RootUiData>) => x.id === beforeId);

      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
        return state;
      }

      const modalToMove = state.queuedModals.get(fromIndex);
      if (!modalToMove) {
        return state;
      }

      let nextQueued = state.queuedModals.delete(fromIndex);
      // If removing an earlier element, the target index shifts left by 1.
      const insertIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
      nextQueued = nextQueued.insert(insertIndex, modalToMove);

      return state.set('queuedModals', nextQueued);
    }
    default:
      return state;
  }
}
export default reducer;
